import { ColorIcon, colors, customEvent, HStack, VStack } from "goi_common";
import { useRouter } from "next/router";
import { useOverlay } from "@toss/use-overlay";

import { FixedCustomButton } from "@/components/FixedCustomButton";
import { OverlayModal } from "goi_common/src/components/OverlayModal";
import {
  hasVisibleBankAccount,
  isEmptyAccountConfirmDismissed,
  setEmptyAccountConfirmDismissed,
  ShareChoiceWithAccountGuard,
} from "@/components/Modals/ShareChoiceWithAccountGuard";
import { useGetObituary } from "../../BugoCreateContainer/hooks/useGetObituary";

export const BugoDetailSubmitFixedButtonFooter = ({
  isShowModifyButton,
}: {
  isShowModifyButton?: boolean;
}) => {
  /** share 인 경우에는 버튼 안보여줘야함  */
  const router = useRouter();
  const { obituaryKey, key: authenticatedKey } = router.query as {
    obituaryKey?: string;
    key?: string;
  };

  const overlay = useOverlay();
  const { data: obituary } = useGetObituary();

  // 계좌 설정 경로: 상주는 본인 계좌 입력 화면, 작성자는 상주 초대(계좌 공유 범위) 화면
  const accountSettingPath = authenticatedKey
    ? `/bankaccount?key=${authenticatedKey}`
    : `/bugo/share/invite-sangju?obituaryKey=${obituaryKey}`;

  // 상주는 본인 링크를 그대로 전달하므로 본인의 공유 범위 설정 기준으로 판정한다.
  // 작성자는 상주를 고른 뒤 공유하므로 /bugo/share/choice 에서 선택 상주 기준으로 판정한다.
  const hasBankAccount = hasVisibleBankAccount(
    obituary?.sangjus ?? [],
    obituary ?? {}
  );
  // 상주용 API는 obituary.key를 내려주지 않는다. 이 경우 응답의 공개 상주 키를
  // 저장 식별자로 사용하고, URL의 authentication key는 localStorage에 남기지 않는다.
  const dismissalObituaryKey =
    obituary?.key ?? obituaryKey ?? obituary?.sangjuInfo?.key;

  const onClickShareWriterOrSangjuVersion = () => {
    overlay.open(({ isOpen, close: onCloseOverlay }) => {
      const onClose = () => {
        onCloseOverlay();
      };

      return (
        <OverlayModal
          closeOnOverlayClick
          isOpen={isOpen}
          onClose={onClose}
          modalContentStyle={{
            maxWidth: "300px",
            borderRadius: "20px",
          }}
          overlayModalContent={
            <ShareChoiceWithAccountGuard
              onClose={onClose}
              needsEmptyAccountConfirm={
                !!authenticatedKey &&
                !hasBankAccount &&
                !isEmptyAccountConfirmDismissed(dismissalObituaryKey)
              }
              onDismissEmptyAccountConfirm={() =>
                setEmptyAccountConfirmDismissed(dismissalObituaryKey, true)
              }
              onClickAccountSetting={() => {
                onClose();
                router.push(accountSettingPath);
              }}
              type={authenticatedKey ? "sangju" : "writer"}
              obituaryKey={authenticatedKey ? undefined : obituaryKey}
            />
          }
        />
      );
    });
  };

  return (
    <FixedCustomButton>
      <VStack gap="16px" width="100%" alignItems="center">
        {isShowModifyButton && (
          <HStack
            as="button"
            gap="4px"
            alignItems="center"
            className="button"
            css={{
              width: "fit-content",
              padding: "12px 24px",
              borderRadius: "30px",
              color: colors.white,

              background: colors.black,
            }}
            onClick={() => {
              customEvent({
                path: "bugo_detail",
                trigger_and_target: "btn_modify-bugo_update",
              });
              router.push(`/bugo/update/${obituaryKey}/`);
              // TODO: 부고 첫번째 작성 페이지로 이동
            }}
          >
            <ColorIcon name="icon-pencil" size="16px" color={colors.white} />
            부고장 수정하기
          </HStack>
        )}
        <HStack gap="9px" width="100%">
          <button
            className="subtitle_16_sb"
            onClick={() => {
              customEvent({
                path: "bugo_detail",
                trigger_and_target: authenticatedKey
                  ? "btn_account-bank_account"
                  : "btn_invite_family-bugo_share",
              });
              router.push(accountSettingPath);
            }}
            css={{
              width: "100%",
              padding: "12px",
              borderRadius: "5px",
              background: colors.white,
              boxShadow: "0px 0px 4px 0px rgba(0, 0, 0, 0.12)",
            }}
          >
            부의금 계좌 입력하기
          </button>
          <button
            className="subtitle_16_sb gray50"
            onClick={() => {
              customEvent({
                path: "bugo_detail",
                trigger_and_target: "btn_share-bugo_share",
              });
              onClickShareWriterOrSangjuVersion();
            }}
            css={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "5px",
              background: colors.warmGray850,
            }}
          >
            부고 전달하기
          </button>
        </HStack>
        {!authenticatedKey && (
          <button
            className="subtitle_16_sb"
            onClick={() => {
              customEvent({
                path: "bugo_detail",
                trigger_and_target: "btn_thanks_message-share",
              });
              router.push(`/thanks-message/share?obituaryKey=${obituaryKey}`);
            }}
            css={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "5px",
              background: colors.white,
              border: `1px solid ${colors.warmGray850}`,
              color: colors.warmGray850,
            }}
          >
            답례문 보내기
          </button>
        )}
      </VStack>
    </FixedCustomButton>
  );
};
